const rootSelector = "[data-rcd-search-select], [data-search-select]";
const optionSelector = "[data-rcd-search-select-option], [data-search-select-option]";

export const setupRcdSearchSelects = (root = document) => {
    root.querySelectorAll(`${rootSelector}:not([data-rcd-search-select-ready])`).forEach((select) => {
        select.dataset.rcdSearchSelectReady = "true";
        select.setAttribute("data-rcd-search-select", "");

        const input = select.querySelector("[data-rcd-search-select-input], [data-search-select-input]");
        const value = select.querySelector("[data-rcd-search-select-value], [data-search-select-value]");
        const menu = select.querySelector("[data-rcd-search-select-menu], [data-search-select-menu]");
        const empty = select.querySelector("[data-rcd-search-select-empty], [data-search-select-empty]");
        if (!(input instanceof HTMLInputElement) || !(value instanceof HTMLInputElement) || !(menu instanceof HTMLElement)) {
            return;
        }

        const getOptions = () => [...select.querySelectorAll(optionSelector)];
        let activeIndex = -1;
        input.setAttribute("role", "combobox");
        input.setAttribute("aria-autocomplete", "list");
        input.setAttribute("aria-expanded", "false");
        menu.setAttribute("role", "listbox");

        const prepareOptions = () => getOptions().forEach((option, index) => {
            option.setAttribute("role", "option");
            option.id ||= `${select.id || "rcd-search-select"}-option-${index}`;
        });
        prepareOptions();

        const visibleOptions = () => getOptions().filter((option) => !option.hidden);

        const syncActive = () => {
            const visible = visibleOptions();
            activeIndex = Math.min(Math.max(activeIndex, -1), visible.length - 1);
            visible.forEach((option, index) => {
                const active = index === activeIndex;
                option.classList.toggle("is-active", active);
                option.setAttribute("aria-selected", String(option.dataset.value === value.value));
            });
            const active = visible[activeIndex];
            if (active) {
                input.setAttribute("aria-activedescendant", active.id);
                active.scrollIntoView({ block: "nearest" });
            } else {
                input.removeAttribute("aria-activedescendant");
            }
        };

        const filter = () => {
            const query = input.value.trim().toLocaleLowerCase();
            let visibleCount = 0;
            prepareOptions();
            getOptions().forEach((option) => {
                const label = option.dataset.label || option.textContent || "";
                const match = !query || label.toLocaleLowerCase().includes(query);
                option.hidden = !match;
                if (match) {
                    visibleCount += 1;
                }
            });
            if (empty) {
                empty.hidden = visibleCount > 0;
            }
            syncActive();
        };

        const open = () => {
            filter();
            menu.hidden = false;
            input.setAttribute("aria-expanded", "true");
        };

        const close = () => {
            menu.hidden = true;
            input.setAttribute("aria-expanded", "false");
            input.removeAttribute("aria-activedescendant");
            activeIndex = -1;
        };

        const choose = (option) => {
            if (!(option instanceof HTMLElement)) {
                return;
            }
            value.value = option.dataset.value || "";
            input.value = option.dataset.label || option.textContent?.trim() || "";
            getOptions().forEach((item) => item.setAttribute("aria-selected", String(item === option)));
            close();
            const detail = { value: value.value, option };
            select.dispatchEvent(new CustomEvent("rcd:search-select:change", { bubbles: true, detail }));
            select.dispatchEvent(new CustomEvent("search-select:change", { bubbles: true, detail }));
        };

        input.addEventListener("focus", open);
        input.addEventListener("input", () => {
            const selectedOption = getOptions().find((option) => option.dataset.value === value.value);
            if (!selectedOption || (selectedOption.dataset.label || "") !== input.value) {
                value.value = "";
            }
            activeIndex = -1;
            open();
        });
        input.addEventListener("keydown", (event) => {
            const visible = visibleOptions();
            if (event.key === "ArrowDown" || event.key === "ArrowUp") {
                event.preventDefault();
                open();
                activeIndex = visible.length
                    ? (activeIndex + (event.key === "ArrowDown" ? 1 : -1) + visible.length) % visible.length
                    : -1;
                syncActive();
            } else if (event.key === "Enter" && !menu.hidden && activeIndex >= 0) {
                event.preventDefault();
                choose(visibleOptions()[activeIndex]);
            } else if (event.key === "Escape") {
                close();
            }
        });
        menu.addEventListener("click", (event) => {
            const option = event.target instanceof Element ? event.target.closest(optionSelector) : null;
            if (option) {
                event.preventDefault();
                choose(option);
            }
        });
        select.addEventListener("rcd:search-select:refresh", filter);
        document.addEventListener("pointerdown", (event) => {
            if (!select.contains(event.target)) {
                close();
            }
        });
    });
};
